import * as React from 'react';

import { ParticipantAvatar, SplitList, StatusChip, type IDataColumn } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IPage } from '../../api/oversight-contracts';
import type {
  IAdminParticipantListIdentifier,
  IAdminParticipantProfile,
  IAdminRosterMember,
  ILatestResponse,
  IRecordAccount,
  ISearchMatch,
  PeopleAccount,
  PeopleDisclosures,
  PeopleRegistry,
  PeopleVerification,
  RecordListRoster,
  RecordListSortKey
} from '../../api/participant-contracts';
import { pageOf } from '../../components/admin-page';
import { ListArea, usePaneRead } from '../../components/list-area';
import {
  CardLink,
  ParticipantLink,
  RECORD_AREA_LABEL,
  RECORD_AREA_PATH,
  recordPathIn,
  type ICardLinkState,
  type RecordArea
} from '../../components/participant-link';
import { instantOf, liveAccountFilter, sortOf } from '../../components/record-filters';
import { RegistryNumberChip } from '../../components/registry-number-chip';
import { ResponseStatusChip } from '../../components/responses-table';
import { ROW_NAME } from '../../components/row-name-click';
import { SearchMatches } from '../../components/search-matches';
import { responsePath } from '../../paths';
import { registryNumbers } from '../../registries';
import { slotWords } from '../../roster-words';
import { profilePlace } from '../person-hub/household';

/**
 * What Teams and Firms share beside their work page (`record-work-page.tsx`): the query the URL
 * names (`recordQueryOf`), the pane beside a record — which re-reads the list under the query it
 * was opened from, with the list's own builder (`IRecordListSpec.read`) — and the cells both draw.
 * survey-api reads every filter over the record itself (record search §3.4); nothing here filters,
 * sorts or counts a row.
 */

/** What each list's row carries that this module draws. */
export interface IRecordRow {
  participantId: string;
  displayName: string;
  photoUri: Nullable<string>;
  identifiers: IAdminParticipantListIdentifier[];
  profile: IAdminParticipantProfile;
  account: Nullable<IRecordAccount>;
  latestResponse: Nullable<ILatestResponse>;
  createdAt: string;
  matches: ISearchMatch[];
}

/** The query both lists send, from the URL — each value as spelled. */
export function recordQueryOf(params: URLSearchParams, page = pageOf(params.get('page'))) {
  const createdSince = params.get('createdSince');
  const sort = params.get('sort');
  return {
    q: params.get('q') ?? '',
    cycle: params.get('cycle') ?? undefined,
    // Sent as the URL spells them: an unknown value is survey-api's to refuse, by name.
    verification: (params.get('verification') ?? undefined) as PeopleVerification | undefined,
    registry: (params.get('registry') ?? undefined) as PeopleRegistry | undefined,
    account: (liveAccountFilter(params.get('account')) ?? undefined) as PeopleAccount | undefined,
    disclosures: (params.get('disclosures') ?? undefined) as PeopleDisclosures | undefined,
    roster: (params.get('roster') ?? undefined) as RecordListRoster | undefined,
    createdSince: createdSince === null ? undefined : instantOf(createdSince),
    sort: sort === null ? undefined : sortOf<RecordListSortKey>(sort),
    page
  };
}

/** What a list's own filter chips are handed: the URL, and how to set one parameter. */
export interface IFilterContext {
  params: URLSearchParams;
  choose: (name: string) => (value: Nullable<string>) => void;
}

export interface IRecordListSpec<Row extends IRecordRow> {
  area: Exclude<RecordArea, 'people'>;
  /** The record in words, one and many — "team", "teams". */
  counted: [string, string];
  placeholder: string;
  /** The help's opening — what the list holds. */
  rule: React.ReactNode;
  /** The URL's parameters past the shared ones that narrow it. */
  filters: readonly string[];
  read: (params: URLSearchParams, page: number, signal: AbortSignal) => Promise<IPage<Row>>;
  /** The list's own chips, drawn before Roster and after it. */
  beforeRoster?: (context: IFilterContext) => React.ReactNode;
  afterRoster?: (context: IFilterContext) => React.ReactNode;
  columns: (search: string, q: string) => IDataColumn<Row>[];
  /** The pane's line under a record's name. */
  secondary: (row: Row) => string;
}

/** A list and its records as one area: the list, or a record page with the list beside it. */
export function RecordListArea<Row extends IRecordRow>({ spec }: { spec: IRecordListSpec<Row> }) {
  return (
    <ListArea
      param="participantId"
      list={RECORD_AREA_PATH[spec.area]}
      paneTitle={RECORD_AREA_LABEL[spec.area]}
      pane={(search, selected) => <RecordPane spec={spec} search={search} selected={selected} />}
    />
  );
}

/** The list as the pane beside a record draws it — one record a line, the open one current. */
function RecordPane<Row extends IRecordRow>({
  spec,
  search,
  selected
}: {
  spec: IRecordListSpec<Row>;
  search: string;
  selected: string;
}) {
  const { shown, pager, status } = usePaneRead(
    search,
    `No ${spec.counted[0]} on this list.`,
    spec.read
  );
  return (
    <SplitList
      label={RECORD_AREA_LABEL[spec.area]}
      selected={shown?.items.some((row) => row.participantId === selected) ? selected : null}
      items={
        shown?.items.map((row) => {
          const numbers = registryNumbers(row.identifiers);
          return {
            key: row.participantId,
            to: recordPathIn(spec.area, row.participantId),
            state: { search, list: true } satisfies ICardLinkState,
            primary: row.displayName,
            secondary: spec.secondary(row),
            trailing: numbers[0] ? <RegistryNumberChip number={numbers[0]} compact /> : undefined
          };
        }) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}

/** The first column: the record's photo and name — the way to its page — its place, an extra
 *  chip where the list draws one, and why `q` found it. */
export function recordNameColumn<Row extends IRecordRow>(
  area: Exclude<RecordArea, 'people'>,
  header: string,
  search: string,
  q: string,
  chip?: (row: Row) => React.ReactNode
): IDataColumn<Row> {
  return {
    id: 'name',
    header,
    cell: (row) => {
      const place = profilePlace(row.profile);
      return (
        <>
          <span className="flex min-w-0 items-center gap-2" {...ROW_NAME}>
            <ParticipantAvatar
              photoUri={row.photoUri}
              displayName={row.displayName}
              size="sm"
              decorative
            />
            <CardLink
              to={recordPathIn(area, row.participantId)}
              search={search}
              fromList
              className="min-w-0 break-words"
            >
              {row.displayName}
            </CardLink>
            {chip?.(row)}
          </span>
          {place && <p className="mt-0.5 text-xs text-muted-foreground">{place}</p>}
          <SearchMatches matches={row.matches} q={q} />
        </>
      );
    }
  };
}

/** The account acting for the record — its address and state — or "No account". */
export function accountColumn<Row extends IRecordRow>(): IDataColumn<Row> {
  return {
    id: 'account',
    header: 'Account',
    cell: (row) =>
      row.account === null ? (
        <span className="text-muted-foreground">No account</span>
      ) : (
        <>
          <span className="break-all">{row.account.email}</span>
          <p className="mt-0.5 text-xs text-muted-foreground">{row.account.status}</p>
        </>
      )
  };
}

/** The record's response changed last: its cycle, state and path, linked to the response. */
export function latestResponseColumn<Row extends IRecordRow>(): IDataColumn<Row> {
  return {
    id: 'latest-response',
    header: 'Latest response',
    cell: ({ latestResponse: latest }) =>
      latest === null ? (
        <span className="text-muted-foreground">No response</span>
      ) : (
        <span className="flex flex-wrap items-center gap-1.5">
          <CardLink to={responsePath(latest.responseId)}>{latest.cycleCode}</CardLink>
          <ResponseStatusChip status={latest.status} />
          {latest.pathCode !== null && <StatusChip>{latest.pathCode}</StatusChip>}
        </span>
      )
  };
}

export function createdColumn<Row extends IRecordRow>(): IDataColumn<Row> {
  return {
    id: 'created',
    header: 'Created',
    optional: true,
    className: 'text-xs whitespace-nowrap text-muted-foreground',
    cell: (row) => formatDateTime(row.createdAt)
  };
}

/**
 * A roster's slots, in the order sent (`GET …/roster`'s members, record search §3.2): each the
 * person a slot resolves to, linked; else the name as the response answered it and why nobody is
 * linked — "not identified", or Veritant's decision of nobody.
 */
export function RosterSlots({
  members,
  label
}: {
  members: readonly IAdminRosterMember[];
  label: string;
}) {
  if (members.length === 0) return <span className="text-muted-foreground">None named</span>;
  return (
    <ul aria-label={label} className="grid gap-1">
      {members.map((slot) => (
        <li key={slot.slot} data-roster-slot={slot.slot}>
          {slot.member !== null ? (
            <ParticipantLink participant={slot.member} className="break-words" />
          ) : (
            <span className="break-words">{answeredName(slot)}</span>
          )}
          <span className="text-xs text-muted-foreground">
            {' '}
            · {slotWords(slot)}
            {slot.member === null &&
              (slot.resolvedBy === 'staff' ? ' · nobody, by Veritant’s decision' : ' · not identified')}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** A slot's member as the response answered them — the name, else the email, else the CRD. */
function answeredName(slot: IAdminRosterMember): string {
  return slot.fullName ?? slot.email ?? (slot.crd === null ? '—' : `CRD ${slot.crd}`);
}
